import { Badge } from "@hortis/ui/badge";
import { IconButton } from "@hortis/ui/button";
import { DialogTrigger } from "@hortis/ui/dialog";
import { Edit2, Plus } from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { format } from "date-fns";
import {
  AccessionFieldsFragment,
  ProvenanceCategoryCode,
} from "generated/graphql";
import {
  RecordSection,
  RecordSectionDataGroup,
  RecordSectionDataItem,
  RecordSectionDataItemLabel,
  RecordSectionDataItemValue,
  RecordSectionTitle,
} from "src/components/record-section";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { WildCollectionDialog } from "src/features/create-accession/components/accession-data-form/wild-collection-dialog";
import { parseUtcDate } from "src/utils/dates/parse-utc-date";
import { twMerge } from "tailwind-merge";
import { extractCollectionDataFormFields } from "../components/edit-drawer/edit-collection-data";

const getHasData = (accession: AccessionFieldsFragment) => {
  return (
    accession.collectionDate != null ||
    accession.collectionCollectors != null ||
    accession.collectionCollectorsFieldNumber != null ||
    accession.collectionHabitat != null ||
    accession.collectionAssociatedTaxa != null ||
    accession.collectionRemarks != null ||
    accession.collectionCountry != null ||
    accession.collectionStateProvince != null ||
    accession.collectionLocality != null ||
    (accession.collectionLatitude != null &&
      accession.collectionLongitude != null) ||
    accession.collectionCoordinateUncertainty != null ||
    accession.collectionElevation != null
  );
};

const hasGeoData = (accession: AccessionFieldsFragment): boolean => {
  return (
    (accession.collectionLatitude != null &&
      accession.collectionLongitude != null) ||
    accession.collectionCoordinateUncertainty != null ||
    accession.collectionElevation != null ||
    accession.collectionStateProvince != null ||
    accession.collectionLocality != null ||
    accession.collectionCountry != null
  );
};

export const AccessionWildCollectionSection = ({
  accession,
  canEdit,
}: { accession: AccessionFieldsFragment; canEdit: boolean }) => {
  const hasData = getHasData(accession);
  const hasGeo = hasGeoData(accession);
  const isWildProvenance =
    accession.provenanceCategoryCode === ProvenanceCategoryCode.W ||
    accession.provenanceCategoryCode === ProvenanceCategoryCode.Z;

  return isWildProvenance && (canEdit || hasData) ? (
    <RecordSection
      className={twMerge(!hasData && "border-dashed shadow-none pb-3")}
      data-cy="wild-collection-section"
    >
      <RecordSectionTitle>
        <div className="flex items-center gap-3">
          Wild collection
          {accession.collectionCountry?.countryCodeAlpha2 != null && (
            <Badge
              color="grey"
              type="icon"
              className="pl-1 border border-grey-200"
            >
              <div className="flex items-start justify-start gap-1.5">
                <img
                  className="h-4 w-4 rounded-full"
                  src={`https://flagcdn.com/w20/${accession.collectionCountry.countryCodeAlpha2.toLowerCase()}.png`}
                  alt={`${accession.collectionCountry.name} flag`}
                />

                <span>{accession.collectionCountry.codeId}</span>
              </div>
            </Badge>
          )}
        </div>
        {canEdit && (
          <WildCollectionDialog
            accessionId={accession.id}
            initialData={
              hasData ? extractCollectionDataFormFields(accession) : undefined
            }
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
                  <IconButton
                    ariaLabel={
                      hasData ? "Edit wild collection" : "Add wild collection"
                    }
                    icon={hasData ? Edit2 : Plus}
                    size="sm"
                    variant="tertiaryGray"
                    testId="wild-collection-edit-button"
                  />
                </DialogTrigger>
              </TooltipTrigger>
              <TooltipContent side="left">
                {hasData
                  ? "Change wild collection details"
                  : "Add wild collection details"}
              </TooltipContent>
            </Tooltip>
          </WildCollectionDialog>
        )}
      </RecordSectionTitle>
      {hasData && (
        <div className="space-y-4">
          <RecordSectionDataGroup>
            {accession.collectionDate != null && (
              <RecordSectionDataItem data-cy="collection-date">
                <RecordSectionDataItemLabel>
                  Collection date
                </RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {format(parseUtcDate(accession.collectionDate), "PPP")}
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
            {accession.collectionCollectors != null && (
              <RecordSectionDataItem data-cy="collection-collectors">
                <RecordSectionDataItemLabel>
                  Collectors(s)
                </RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {accession.collectionCollectors}
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
            {accession.collectionCollectorsFieldNumber != null && (
              <RecordSectionDataItem data-cy="collection-collectors-reference">
                <RecordSectionDataItemLabel>
                  Collectors(s) reference
                </RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {accession.collectionCollectorsFieldNumber}
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
            {accession.collectionHabitat != null && (
              <RecordSectionDataItem data-cy="collection-habitat">
                <RecordSectionDataItemLabel>Habitat</RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {accession.collectionHabitat ?? "-"}
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
            {accession.collectionAssociatedTaxa != null && (
              <RecordSectionDataItem data-cy="collection-associated-taxa">
                <RecordSectionDataItemLabel>
                  Associated taxa
                </RecordSectionDataItemLabel>
                <RecordSectionDataItemValue
                  className="whitespace-pre-line break-normal"
                  style={{ wordBreak: "break-word" }}
                >
                  {accession.collectionAssociatedTaxa}
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
            {accession.collectionRemarks != null && (
              <RecordSectionDataItem data-cy="collection-remarks">
                <RecordSectionDataItemLabel>Remarks</RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {accession.collectionRemarks}
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
          </RecordSectionDataGroup>
          {hasGeo && (
            <>
              <div className="border-b border-dashed border-grey-200 my-4" />
              <RecordSectionDataGroup>
                {accession.collectionCountry?.name != null && (
                  <RecordSectionDataItem data-cy="collection-country">
                    <RecordSectionDataItemLabel>
                      Country
                    </RecordSectionDataItemLabel>
                    <RecordSectionDataItemValue>
                      {accession.collectionCountry.name}
                    </RecordSectionDataItemValue>
                  </RecordSectionDataItem>
                )}
                {accession.collectionStateProvince != null && (
                  <RecordSectionDataItem data-cy="collection-province-region">
                    <RecordSectionDataItemLabel>
                      Province / region
                    </RecordSectionDataItemLabel>
                    <RecordSectionDataItemValue>
                      {accession.collectionStateProvince}
                    </RecordSectionDataItemValue>
                  </RecordSectionDataItem>
                )}
                {accession.collectionLocality != null && (
                  <RecordSectionDataItem data-cy="collection-locality">
                    <RecordSectionDataItemLabel>
                      Locality
                    </RecordSectionDataItemLabel>
                    <RecordSectionDataItemValue>
                      {accession.collectionLocality}
                    </RecordSectionDataItemValue>
                  </RecordSectionDataItem>
                )}
                {accession.collectionLatitude != null &&
                  accession.collectionLongitude != null && (
                    <RecordSectionDataItem data-cy="collection-coordinates">
                      <RecordSectionDataItemLabel>
                        Coordinates
                      </RecordSectionDataItemLabel>
                      <RecordSectionDataItemValue>
                        {`${accession.collectionLatitude}, ${accession.collectionLongitude}`}
                      </RecordSectionDataItemValue>
                    </RecordSectionDataItem>
                  )}
                {accession.collectionCoordinateUncertainty != null && (
                  <RecordSectionDataItem data-cy="collection-uncertainty">
                    <RecordSectionDataItemLabel>
                      Uncertainty (m)
                    </RecordSectionDataItemLabel>
                    <RecordSectionDataItemValue>
                      {accession.collectionCoordinateUncertainty}
                    </RecordSectionDataItemValue>
                  </RecordSectionDataItem>
                )}
                {accession.collectionElevation != null && (
                  <RecordSectionDataItem data-cy="collection-elevation">
                    <RecordSectionDataItemLabel>
                      Elevation (m)
                    </RecordSectionDataItemLabel>
                    <RecordSectionDataItemValue>
                      {accession.collectionElevation}
                    </RecordSectionDataItemValue>
                  </RecordSectionDataItem>
                )}
              </RecordSectionDataGroup>
            </>
          )}
        </div>
      )}
    </RecordSection>
  ) : (
    <></>
  );
};
